import EvaluationFlow from "../../flow/EvaluationFlow"
import { InAppMessageRequest } from "./InAppMessageRequest"
import { InAppMessageEvaluation } from "./InAppMessageEvaluation"
import FlowEvaluator from "../../flow/FlowEvaluator"
import { EvaluatorContext } from "../Evaluator"
import { DecisionReason } from "../../../model/model"
import { InAppMessageFrequencyCapMatcher, InAppMessageMatcher } from "../../target/InAppMessageMatcher"
import InAppMessageResolver from "../../target/InAppMessageResolver"

export type InAppMessageFlow = EvaluationFlow<InAppMessageRequest, InAppMessageEvaluation>

export default interface InAppMessageFlowEvaluator extends FlowEvaluator<InAppMessageRequest, InAppMessageEvaluation> {
  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined
}

export class PlatformInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const isWebPlatform = request.inAppMessage.supports("WEB")
    if (!isWebPlatform) {
      return InAppMessageEvaluation.of(request, context, DecisionReason.UNSUPPORTED_PLATFORM)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class OverrideInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  constructor(
    private readonly userOverrideMatcher: InAppMessageMatcher,
    private readonly inAppMessageResolver: InAppMessageResolver
  ) {}

  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const isOverrideMatched = this.userOverrideMatcher.matches(request, context)
    if (isOverrideMatched) {
      const message = this.inAppMessageResolver.resolve(request, context)
      return InAppMessageEvaluation.of(request, context, DecisionReason.OVERRIDDEN, message)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class DraftInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const isDraft = request.inAppMessage.status === "DRAFT"
    if (isDraft) {
      return InAppMessageEvaluation.of(request, context, DecisionReason.IN_APP_MESSAGE_DRAFT)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class PauseInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const isPause = request.inAppMessage.status === "PAUSE"
    if (isPause) {
      return InAppMessageEvaluation.of(request, context, DecisionReason.IN_APP_MESSAGE_PAUSED)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class PeriodInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const isWithinPeriod = request.inAppMessage.period.within(request.timestamp)
    if (!isWithinPeriod) {
      return InAppMessageEvaluation.of(request, context, DecisionReason.NOT_IN_IN_APP_MESSAGE_PERIOD)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class HiddenInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  constructor(private readonly hiddenMatcher: InAppMessageMatcher) {}

  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const isHidden = this.hiddenMatcher.matches(request, context)
    if (isHidden) {
      return InAppMessageEvaluation.of(request, context, DecisionReason.IN_APP_MESSAGE_HIDDEN)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class TargetInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  constructor(private readonly targetMatcher: InAppMessageMatcher) {}

  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const isTargetMatched = this.targetMatcher.matches(request, context)
    if (!isTargetMatched) {
      return InAppMessageEvaluation.of(request, context, DecisionReason.NOT_IN_IN_APP_MESSAGE_TARGET)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class FrequencyInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  constructor(private readonly frequencyCapMatcher: InAppMessageFrequencyCapMatcher) {}
  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const isFrequencyCapped = this.frequencyCapMatcher.matches(request, context)
    if (isFrequencyCapped) {
      return InAppMessageEvaluation.of(request, context, DecisionReason.IN_APP_MESSAGE_FREQUENCY_CAPPED)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class ExperimentInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  constructor(private readonly inAppMessageResolver: InAppMessageResolver) {}

  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const message = this.inAppMessageResolver.resolve(request, context)
    const isControlGroup = message.layout.displayType === "NONE"
    if (isControlGroup) {
      return InAppMessageEvaluation.of(request, context, DecisionReason.EXPERIMENT_CONTROL_GROUP)
    }

    return nextFlow.evaluate(request, context)
  }
}

export class MessageResolutionInAppMessageFlowEvaluator implements InAppMessageFlowEvaluator {
  constructor(private readonly inAppMessageResolver: InAppMessageResolver) {}

  evaluate(
    request: InAppMessageRequest,
    context: EvaluatorContext,
    nextFlow: InAppMessageFlow
  ): InAppMessageEvaluation | undefined {
    const message = this.inAppMessageResolver.resolve(request, context)
    return InAppMessageEvaluation.of(request, context, DecisionReason.IN_APP_MESSAGE_TARGET, message)
  }
}
